import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import * as XLSX from "xlsx";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import IconButton from "../../packages/IconButton";
import ExportErrorIcon from "../../packages/SVGs/ExportErrorIcon";
import WPTertiaryButton from "../../packages/WPTertiaryButton";

import SupportedFileInfo from "./SupportedFileInfo";

import {
  CustomModalButtons,
  FILE_UPLOAD,
} from "../../constants/global.constants";

import { excelIcon, failureIcon, successIcon } from "../../assets";
import { ArrowRightIcon, RemoveIcon } from "../../assets/icons";

import { getSupportedFileTypes } from "../../utils/appUtils";
import { delay } from "../../utils/Utils";

import "./FileUploadCard.scss";

const XLSX_READABLE_EXTENSIONS = new Set(["xls", "xlsx", "csv"]);

const FileUploadCard = ({
  uploadedFile,
  setUploadedFile,
  onValidHandler,
  tempExistingCIPFileName,
  onAppendHandler,
  isUploadComplete,
  appendStatus,
  showRemoveIcon,
  templateFileName,
  handleAppendReplace,
  handleRemoveFile,
  successMessage,
  showAppend = false,
  showDeleteConfirmation = false,
  fileImg = excelIcon,
  primaryActionLabel = "fileUpload.upload",
  primaryActionIcon = <ArrowRightIcon />,
  onTemplateDownload,
  supportedFileTypes = FILE_UPLOAD.SUPPORTED_FILE_TYPES,
  maxFileSize = FILE_UPLOAD.MAX_FILE_SIZE,
  hideReplaceOnSuccess = false, // hide the replace button when upload is successful
}) => {
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [showRemoveFileConfirmation, setShowRemoveFileConfirmation] =
    useState(false);
  const fileInputRef = useRef();
  const { t } = useTranslation();

  const acceptedFileTypes = getSupportedFileTypes(
    supportedFileTypes,
    ".",
    true,
  );

  const OnRemoveFileClick = () => {
    if (showDeleteConfirmation) {
      setShowRemoveFileConfirmation(true);
      return;
    }
    handleRemoveFile();
  };

  const onRemoveConfirmationClose = (id) => {
    setShowRemoveFileConfirmation(false);

    if (id === CustomModalButtons.CONFIRM) {
      handleRemoveFile();
    }
  };

  const validateFile = useCallback(
    (file) => {
      return new Promise(async (resolve) => {
        setStatus("loading");
        setProgress(20);

        const fileExtension = file.name.split(".").pop().toLowerCase();
        const isValidType = supportedFileTypes.some((type) =>
          type.toLowerCase().includes(fileExtension),
        );

        if (!isValidType) {
          await delay(400);
          setErrorMsg(t("fileUpload.invalidFileType"));
          setStatus("error");
          resolve(false);
          return;
        }

        setProgress(40);

        const maxSizeInBytes = parseInt(maxFileSize, 10) * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
          setProgress(60);
          await delay(400);
          setErrorMsg(t("fileUpload.fileTooLarge"));
          setStatus("error");
          resolve(false);
          return;
        }

        // Validate workbook content only for file types XLSX can parse reliably.
        if (!XLSX_READABLE_EXTENSIONS.has(fileExtension)) {
          await delay(400);
          setProgress(80);
          resolve(true);
          return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const workbook = XLSX.read(reader.result, { type: "array" });

            const allSheetsEmpty = workbook.SheetNames.every((sheetName) => {
              const sheet = workbook.Sheets[sheetName];
              const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
              return (
                data.length === 0 || (data.length === 1 && data[0].length === 0)
              );
            });

            if (allSheetsEmpty) {
              await delay(400);
              setErrorMsg(t("fileUpload.noData"));
              setStatus("error");
              resolve(false);
              return;
            }

            await delay(400);
            setProgress(80);
            resolve(true);
          } catch (err) {
            setErrorMsg(t("fileUpload.validationError"));
            setStatus("error");
            resolve(false);
          }
        };

        reader.onerror = () => {
          setErrorMsg(t("fileUpload.validationError"));
          setStatus("error");
          resolve(false);
        };

        reader.readAsArrayBuffer(file);
      });
    },
    [maxFileSize, supportedFileTypes, t],
  );

  const handleReplaceClick = () => {
    if (appendStatus === "success") {
      handleAppendReplace();
      fileInputRef.current.click();
    } else {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const newFile = e.target.files[0];
    if (newFile) {
      setUploadedFile(newFile);
      e.target.value = "";
    }
  };

  const handleUploadClick = async () => {
    const isValid = await validateFile(uploadedFile);
    if (isValid) {
      const response = await onValidHandler();
      const { error } = response || {};
      if (error) {
        setProgress(90);
        await delay(400);
        setErrorMsg(error);
        setStatus("error");
      } else {
        setProgress(100);
        await delay(400);
        setStatus("success");
      }
    }
  };

  useEffect(() => {
    if (uploadedFile || tempExistingCIPFileName) {
      setErrorMsg(null);
      setStatus("idle");
      setProgress(0);
    }
  }, [uploadedFile, tempExistingCIPFileName]);

  return (
    <div>
      <div className="ro-card-border">
        <div className="file-upload-card">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept={acceptedFileTypes}
          />
          <img src={fileImg} alt="file symbol" className="file-img" />
          <div className="file-info">
            <div className="file-name">
              {uploadedFile ? uploadedFile.name : tempExistingCIPFileName}
            </div>
            <div className="file-details">
              {status === "loading" && uploadedFile && (
                <div className="status-loader">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span>{t("fileUpload.validating")}</span>
                </div>
              )}
              {status === "error" && (
                <span className="status-text error">
                  <img src={failureIcon} alt="Failure Icon" /> {errorMsg}
                </span>
              )}
              {(status === "success" || appendStatus === "success") && (
                <span className="status-text success">
                  <img src={successIcon} alt="Success Icon" />{" "}
                  {successMessage || t("fileUpload.uploaded")}
                </span>
              )}
            </div>
          </div>
          <div className="actions">
            {!(hideReplaceOnSuccess === true && status === "success") && (
              <WPTertiaryButton
                onClick={handleReplaceClick}
                disabled={status === "loading"}
                label={t("fileUpload.replace")}
              />
            )}
            {uploadedFile &&
              appendStatus !== "success" &&
              status !== "error" &&
              (!isUploadComplete && status !== "success" ? (
                <WPTertiaryButton
                  variant="contained"
                  onClick={handleUploadClick}
                  disabled={status === "loading"}
                  label={t(primaryActionLabel)}
                  icon={primaryActionIcon}
                  className="upload-button"
                />
              ) : (
                showAppend && (
                  <WPTertiaryButton
                    variant="contained"
                    onClick={onAppendHandler}
                    disabled={status === "loading"}
                    label={t("dataAppend.appendData")}
                    icon={<ExportErrorIcon />}
                    iconPlacement="before"
                    className="upload-button"
                  />
                )
              ))}
            {/* Keeping showremove icon temporarily because it is not being shown in Bulk yet */}
            {(uploadedFile || tempExistingCIPFileName) &&
              showRemoveIcon &&
              isUploadComplete && (
                <IconButton
                  onClick={OnRemoveFileClick}
                  disabled={status === "loading"}
                  className="remove-button"
                >
                  <RemoveIcon
                    className={`remove-button-icon${
                      status === "loading" ? " is-loading" : ""
                    }`}
                  />
                </IconButton>
              )}
            {showRemoveFileConfirmation && (
              <ConfirmationPopup
                header={t("fileUpload.removeFileHeader")}
                description={t("fileUpload.removeFileContent")}
                type={DIALOG_TYPES.ERROR}
                onClose={onRemoveConfirmationClose}
                confirmBtn={t("ValueStrings.remove")}
                cancelBtn={t("ValueStrings.cancel")}
              />
            )}
          </div>
        </div>
      </div>
      <SupportedFileInfo
        supportedFileTypes={supportedFileTypes}
        maxFileSize={maxFileSize}
        templateFileName={templateFileName}
        onTemplateDownload={onTemplateDownload}
      />
    </div>
  );
};

export default FileUploadCard;
